import React, { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { ShoppingCart, Wifi, Smartphone, Package, Filter } from 'lucide-react'
import { productService } from '@/services/products'
import { useCartStore } from '@/stores/cartStore'
import { Product } from '@/types'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { LoadingScreen } from '@/components/ui/Spinner'
import { Alert } from '@/components/ui/Alert'
import SEOHead from '@/components/SEOHead'

// Network filter configuration
// roamingOnly: only shown on roaming pages
// nonRoaming: hidden on roaming pages
const networkFilters = [
  { id: 'all', name: 'All', logo: null },
  { id: 'three', name: 'Three', logo: '/images/networks/three.png', nonRoaming: true },
  { id: 'multi_network', name: 'MultiSim', logo: '/images/networks/multinetwork.png', btnPadding: 3, nonRoaming: true },
  { id: 'o2', name: 'O2', logo: '/images/networks/o2.png', nonRoaming: true },
  { id: 'vodafone', name: 'Vodafone', logo: '/images/networks/vodafone.png', nonRoaming: true },
  { id: 'ee', name: 'EE', logo: '/images/networks/ee.png', nonRoaming: true },
  { id: 'fixed_ip', name: 'Fixed IP', logo: '/images/networks/fixed-ip.png', imgScale: 1.6, nonRoaming: true },
  { id: 'roaming_multinet', name: 'Euro Multi', logo: '/images/networks/roaming-multinet.png', imgScale: 1.8, roamingOnly: true },
  // TEMPORARILY HIDDEN — uncomment to re-enable World Multi and Zone filters
  // { id: 'world_multinet', name: 'World Multi', logo: '/images/networks/roaming-multinet.png', imgScale: 1.8, roamingOnly: true },
  // { id: 'roaming_zone1', name: 'Zone 1', logo: '/images/networks/roaming-zone1.png', imgScale: 1.8, roamingOnly: true },
  // { id: 'roaming_zone2', name: 'Zone 2', logo: '/images/networks/roaming-zone2.png', imgScale: 1.8, roamingOnly: true },
  // { id: 'roaming_zone3', name: 'Zone 3', logo: '/images/networks/roaming-zone3.png', imgScale: 1.8, roamingOnly: true },
  // { id: 'roaming_zone4', name: 'Zone 4', logo: '/images/networks/roaming-zone4.png', imgScale: 1.8, roamingOnly: true },
  // { id: 'roaming_zone5', name: 'Zone 5', logo: '/images/networks/roaming-zone5.png', imgScale: 1.8, roamingOnly: true },
  // { id: 'roaming_zone6', name: 'Zone 6', logo: '/images/networks/roaming-zone6.png', imgScale: 1.8, roamingOnly: true },
]

// Network sort order (lower number = higher priority)
const networkSortOrder: Record<string, number> = {
  'three': 1,
  'multi_network': 2,
  'o2': 3,
  'vodafone': 4,
  'ee': 5,
  'fixed_ip': 6,
  'roaming_multinet': 7,
  'world_multinet': 8,
  'roaming_zone1': 9,
  'roaming_zone2': 10,
  'roaming_zone3': 11,
  'roaming_zone4': 12,
  'roaming_zone5': 13,
  'roaming_zone6': 14,
}

export const Shop: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  // Network filter is seeded from the URL (?network=) so returning from a
  // product detail page keeps the network the user was viewing.
  const [networkFilter, setNetworkFilter] = useState<string>(
    () => new URLSearchParams(location.search).get('network') || 'all'
  )
  const [multisimFilter, setMultisimFilter] = useState<string>('multisim_3net')
  // ?term=1 (rolling monthly) etc. — lets landing pages link straight to a term.
  const [contractLengthFilter, setContractLengthFilter] = useState<string>(
    () => new URLSearchParams(location.search).get('term') || 'all'
  )
  const [simTypeFilter, setSimTypeFilter] = useState<string>('all')
  const [broadbandTypeFilter, setBroadbandTypeFilter] = useState<string>('all')
  const [addedProduct, setAddedProduct] = useState<string | null>(null)
  const { addItem, getItemCount } = useCartStore()

  // Reset filters when navigating to a different category page. The network
  // filter is restored from the URL rather than forced to 'all', so a Back
  // navigation from a product (which returns to e.g. /sim-only?network=o2)
  // preserves the selected network.
  useEffect(() => {
    setNetworkFilter(new URLSearchParams(location.search).get('network') || 'all')
    setContractLengthFilter(new URLSearchParams(location.search).get('term') || 'all')
    setSimTypeFilter('all')
    setBroadbandTypeFilter('all')
  }, [location.pathname])

  // Persist the selected network in the URL (replace — no history spam) so the
  // browser Back button from a product page restores it.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (networkFilter === 'all') params.delete('network')
    else params.set('network', networkFilter)
    const next = params.toString()
    if (next !== location.search.replace(/^\?/, '')) {
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true })
    }
  }, [networkFilter])

  // Determine page type and subcategory from URL
  const { pageType, subCategory } = useMemo(() => {
    const path = location.pathname

    // SIM Only subcategories
    if (path === '/sim-only') return { pageType: 'sim_only', subCategory: null }
    if (path === '/sim-only/data-only') return { pageType: 'sim_only', subCategory: 'data_only' }
    if (path === '/sim-only/calls-text-data') return { pageType: 'sim_only', subCategory: 'calls_text_data' }
    if (path === '/sim-only/multisim') return { pageType: 'sim_only', subCategory: 'multisim_3net' }
    if (path === '/sim-only/multisim-3net') return { pageType: 'sim_only', subCategory: 'multisim_3net' }
    if (path === '/sim-only/multisim-4net') return { pageType: 'sim_only', subCategory: 'multisim_4net' }
    if (path === '/sim-only/roaming') return { pageType: 'sim_only', subCategory: 'roaming' }
    if (path === '/sim-only/guarddata-sim') return { pageType: 'sim_only', subCategory: 'guarddata_sim' }
    if (path === '/sim-only/longlife-sim') return { pageType: 'sim_only', subCategory: 'longlife_sim' }

    // Broadband subcategories
    if (path === '/broadband') return { pageType: 'broadband', subCategory: null }
    if (path === '/broadband/premium') return { pageType: 'broadband', subCategory: 'premium' }
    if (path === '/broadband/lite') return { pageType: 'broadband', subCategory: 'lite' }
    if (path === '/broadband/mifi') return { pageType: 'broadband', subCategory: 'mifi' }

    if (path === '/bundles') return { pageType: 'bundle', subCategory: null }
    return { pageType: 'shop', subCategory: null }
  }, [location.pathname])

  // Build query filters based on page type and subcategory
  const queryFilters = useMemo(() => {
    const filters: any = { is_public: true }

    if (pageType === 'shop') {
      filters.product_type = 'hardware'
      filters.show_in_shop = true
    } else {
      filters.product_type = pageType

      // Add subcategory filters
      if (subCategory) {
        if (pageType === 'sim_only') {
          if (subCategory === 'multisim_3net' || subCategory === 'multisim_4net') {
            filters.network_type = 'multi_network'
            filters.multisim_category = subCategory
          } else if (subCategory === 'roaming') {
            // Load all sim_only and filter client-side to roaming types
          } else if (subCategory === 'guarddata_sim') {
            filters.is_backup_sim = true
          } else if (subCategory === 'longlife_sim') {
            filters.is_longlife_sim = true
          } else {
            filters.sim_type = subCategory
          }
        } else if (pageType === 'broadband') {
          filters.broadband_category = subCategory
        }
      }
    }

    return filters
  }, [pageType, subCategory])

  const { data: products, isLoading, error } = useQuery({
    queryKey: ['products', pageType, subCategory],
    queryFn: () => productService.getProducts(queryFilters),
  })

  // Calculate which networks have products available
  const availableNetworks = useMemo(() => {
    if (!products) return new Set<string>()
    const networks = new Set<string>()
    products.forEach((p) => {
      if (p.network_type) {
        networks.add(p.network_type)
        // If any roaming zone products exist, also expose the world_multinet grouped filter
        if (p.network_type?.startsWith('roaming_zone')) {
          networks.add('world_multinet')
        }
      }
    })
    return networks
  }, [products])

  // Whether to show multisim subcategory picker (on non-multisim pages when MultiSim network selected)
  const showMultisimPicker = networkFilter === 'multi_network' &&
    subCategory !== 'multisim_3net' && subCategory !== 'multisim_4net'

  // Filter by network, multisim subcategory, contract length, and sort by network order
  const filteredAndSortedProducts = useMemo(() => {
    if (!products) return []

    let filtered = products

    // Sub-type filter on "All SIMs" page
    if (pageType === 'sim_only' && subCategory === null && simTypeFilter !== 'all') {
      if (simTypeFilter === 'data_only') {
        filtered = filtered.filter((p) => p.sim_type === 'data_only' && p.network_type !== 'multi_network')
      } else if (simTypeFilter === 'calls_text_data') {
        filtered = filtered.filter((p) => p.sim_type === 'calls_text_data' && p.network_type !== 'multi_network')
      } else if (simTypeFilter === 'multi_network') {
        filtered = filtered.filter((p) => p.network_type === 'multi_network')
      } else if (simTypeFilter === 'roaming') {
        filtered = filtered.filter((p) => p.network_type?.startsWith('roaming_'))
      }
    }

    // Sub-type filter on "All Broadband" page
    if (pageType === 'broadband' && subCategory === null && broadbandTypeFilter !== 'all') {
      filtered = filtered.filter((p) => p.broadband_category === broadbandTypeFilter)
    }

    // If on the roaming page, always filter to only roaming products (Euro MultiNet + all zones)
    if (subCategory === 'roaming') {
      filtered = filtered.filter((p) => p.network_type?.startsWith('roaming_'))
    }

    if (networkFilter === 'world_multinet') {
      filtered = filtered.filter((p) => p.network_type?.startsWith('roaming_zone'))
    } else if (networkFilter !== 'all') {
      filtered = filtered.filter((p) => p.network_type === networkFilter)
    }

    // Filter by multisim subcategory when MultiSim network is selected on non-multisim pages
    if (showMultisimPicker) {
      filtered = filtered.filter((p) => p.multisim_category === multisimFilter)
    }

    // Filter by contract length
    if (contractLengthFilter !== 'all') {
      if (contractLengthFilter === 'payg') {
        filtered = filtered.filter((p) => p.is_pay_and_go || (!p.is_contract))
      } else {
        const months = parseInt(contractLengthFilter)
        filtered = filtered.filter((p) => p.is_contract && p.contract_months === months)
      }
    }

    // Then sort by network order (Three first, then MultiNetwork, O2, Vodafone, EE)
    // Within each network, sort by price (cheapest first)
    return filtered.sort((a, b) => {
      const orderA = networkSortOrder[a.network_type || ''] || 99
      const orderB = networkSortOrder[b.network_type || ''] || 99

      // First compare by network order
      if (orderA !== orderB) {
        return orderA - orderB
      }

      // Within same network, sort by effective price (use discounted_price if available)
      const priceA = (a.discounted_price && a.discounted_price < a.price) ? a.discounted_price : a.price
      const priceB = (b.discounted_price && b.discounted_price < b.price) ? b.discounted_price : b.price
      return priceA - priceB
    })
  }, [products, networkFilter, multisimFilter, showMultisimPicker, contractLengthFilter, simTypeFilter, broadbandTypeFilter, pageType, subCategory])

  const getProductIcon = (type: string) => {
    switch (type) {
      case 'sim_only':
        return <Smartphone className="w-8 h-8 text-primary-600" />
      case 'broadband':
        return <Wifi className="w-8 h-8 text-primary-600" />
      case 'bundle':
        return <Package className="w-8 h-8 text-primary-600" />
      default:
        return <Smartphone className="w-8 h-8 text-primary-600" />
    }
  }

  const handleAddToCart = (product: Product) => {
    addItem(product)
    setAddedProduct(product.name)
    setTimeout(() => setAddedProduct(null), 3000)
  }

  // Page titles and descriptions - MUST be before any early returns
  const pageContent = useMemo(() => {
    // SIM Only subcategories
    if (pageType === 'sim_only') {
      if (subCategory === 'data_only') {
        return {
          title: 'Data Only SIMs',
          description: 'Perfect for tablets, dongles, and devices that only need data'
        }
      }
      if (subCategory === 'calls_text_data') {
        return {
          title: 'Calls, Text & Data SIMs',
          description: 'Complete mobile plans with calls, texts, and data included'
        }
      }
      if (subCategory === 'multisim_3net') {
        return {
          title: 'MultiSim - Vodafone / Three / O2',
          description: 'Multi-network SIMs with coverage across Vodafone, Three, and O2'
        }
      }
      if (subCategory === 'multisim_4net') {
        return {
          title: 'MultiSim - Vodafone / Three / O2 / EE',
          description: 'Multi-network SIMs with coverage across Vodafone, Three, O2, and EE'
        }
      }
      if (subCategory === 'roaming') {
        return {
          title: 'Roaming SIMs',
          description: 'Data SIMs that work internationally across multiple countries and zones'
        }
      }
      // Own title + description — both pages fell back to "SIM Only Deals" with identical
      // content, which Semrush reported as duplicate content (2026-10-03).
      if (subCategory === 'guarddata_sim') {
        return {
          title: 'GuardData Backup SIMs',
          description: 'Backup data SIMs to keep alarms, routers and devices connected if the main line drops'
        }
      }
      if (subCategory === 'longlife_sim') {
        return {
          title: 'O2 Long-Life SIMs',
          description: 'O2 long-life SIMs for devices that need a SIM kept active with minimal use'
        }
      }
      return {
        title: 'SIM Only Deals',
        description: 'Great value SIM only plans with no contract lock-in'
      }
    }

    // Broadband subcategories
    if (pageType === 'broadband') {
      if (subCategory === 'premium') {
        return {
          title: 'Premium Broadband',
          description: 'High-speed broadband for demanding users and businesses'
        }
      }
      if (subCategory === 'lite') {
        return {
          title: 'Lite Broadband',
          description: 'Affordable broadband for everyday browsing and streaming'
        }
      }
      if (subCategory === 'mifi') {
        return {
          title: 'Mifi Portable',
          description: 'Take your internet on the go with portable Mifi devices'
        }
      }
      return {
        title: 'Broadband Packages',
        description: 'Fast and reliable broadband for your home or business'
      }
    }

    if (pageType === 'bundle') {
      return {
        title: 'Bundle Deals',
        description: 'Save more with our mobile and broadband bundles'
      }
    }

    return {
      title: 'Shop',
      description: 'Browse our range of hardware and accessories — one-off prices, no contract required'
    }
  }, [pageType, subCategory])

  if (isLoading && pageType !== 'multisim_landing') {
    return <LoadingScreen message="Loading products..." />
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
      {/* SEO — skip /sim-only and /broadband, which are managed by the admin
          page_seo system via RouteSEO. All other shop/category routes had no
          meta at all, so derive it from the page title/description here. */}
      {!['/sim-only', '/broadband'].includes(location.pathname) && (
        <SEOHead
          title={`${pageContent.title} | Silux Mobile`}
          description={pageContent.description}
          canonical={`https://www.siluxmobile.co.uk${location.pathname === '/sim-only/multisim' ? '/sim-only/multisim-3net' : location.pathname}`}
        />
      )}
      <div className="mb-6 sm:mb-8">
        {/* Page Title */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl xs:text-3xl sm:text-4xl font-bold text-gray-900">{pageContent.title}</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            {pageContent.description}
          </p>
        </div>

        {/* Sub-type filter pills — shown on "All SIMs" and "All Broadband" pages */}
        {pageType === 'sim_only' && subCategory === null && (
          <div className="mb-5">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Filter by Type</p>
            <div className="overflow-x-auto pb-1 -mx-1">
              <div className="flex gap-2 px-1 min-w-max">
                {[
                  { id: 'all', label: 'All SIMs' },
                  { id: 'calls_text_data', label: 'Calls, Text & Data' },
                  { id: 'data_only', label: 'Data Only' },
                  { id: 'multi_network', label: 'Multi-Network' },
                  { id: 'roaming', label: 'Roaming' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setSimTypeFilter(opt.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
                      simTypeFilter === opt.id
                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {pageType === 'broadband' && subCategory === null && (
          <div className="mb-5">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Filter by Type</p>
            <div className="overflow-x-auto pb-1 -mx-1">
              <div className="flex gap-2 px-1 min-w-max">
                {[
                  { id: 'all', label: 'All Broadband' },
                  { id: 'premium', label: 'Premium' },
                  { id: 'lite', label: 'Lite' },
                  { id: 'mifi', label: 'Mifi Portable' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setBroadbandTypeFilter(opt.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
                      broadbandTypeFilter === opt.id
                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* MultiSim Subcategory Selector - on dedicated multisim pages */}
        {(subCategory === 'multisim_3net' || subCategory === 'multisim_4net') && !showMultisimPicker && (
          <div className="flex items-center gap-4 mb-5">
            <button
              onClick={() => navigate('/sim-only/multisim-3net')}
              className={`flex flex-col items-center p-2 sm:p-3 rounded-xl transition-all ${
                subCategory === 'multisim_3net'
                  ? 'bg-purple-100 border-2 border-purple-500 shadow-md'
                  : 'bg-white border-2 border-gray-200 hover:border-purple-300 hover:shadow-sm'
              }`}
            >
              <img
                src="/images/networks/multisim-3net.png"
                alt="MultiSim 3 Networks"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex' }}
              />
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full items-center justify-center text-white font-bold text-xs hidden ${
                subCategory === 'multisim_3net' ? 'bg-purple-500' : 'bg-gray-400'
              }`}>3N</div>
              <span className={`text-xs sm:text-sm mt-1 font-medium text-center leading-tight ${
                subCategory === 'multisim_3net' ? 'text-purple-700' : 'text-gray-600'
              }`}>
                Vodafone / Three / O2
              </span>
            </button>

            <button
              onClick={() => navigate('/sim-only/multisim-4net')}
              className={`flex flex-col items-center p-2 sm:p-3 rounded-xl transition-all ${
                subCategory === 'multisim_4net'
                  ? 'bg-purple-100 border-2 border-purple-500 shadow-md'
                  : 'bg-white border-2 border-gray-200 hover:border-purple-300 hover:shadow-sm'
              }`}
            >
              <img
                src="/images/networks/multisim-4net.png"
                alt="MultiSim 4 Networks"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex' }}
              />
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full items-center justify-center text-white font-bold text-xs hidden ${
                subCategory === 'multisim_4net' ? 'bg-purple-500' : 'bg-gray-400'
              }`}>4N</div>
              <span className={`text-xs sm:text-sm mt-1 font-medium text-center leading-tight ${
                subCategory === 'multisim_4net' ? 'text-purple-700' : 'text-gray-600'
              }`}>
                Vodafone / Three / O2 / EE
              </span>
            </button>

            <div className="border-l border-gray-200 pl-4 max-w-xs">
              <p className="text-xs text-gray-500 leading-relaxed">
                Multi Network Sim -<br />
                3 Networks - Vodafone / Three / O2 Networks<br />
                4 Networks - Vodafone / Three / O2 / EE Networks<br />
                Switch between the networks as you need. Got poor signal on one, switch to another in a moment.
              </p>
            </div>
          </div>
        )}

        {/* Network Filter Icons — hidden on hardware shop page */}
        {pageType !== 'shop' ? (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Filter by Network</span>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/cart'}
                className="flex items-center text-sm"
              >
                <ShoppingCart className="w-4 h-4 mr-1.5" />
                Cart ({getItemCount()})
              </Button>
            </div>
            <div className="overflow-x-auto pb-2 -mx-1">
              <div className="flex items-start gap-3 px-1 min-w-max">
                {networkFilters.map((network) => {
                  // On roaming pages: always show all roaming icons; hide non-roaming icons
                  if (subCategory === 'roaming') {
                    if ((network as any).nonRoaming) return null
                    // Always show roaming icons (even if no products assigned yet)
                    if (network.id !== 'all' && !(network as any).roamingOnly) return null
                  } else {
                    // On non-roaming pages: hide roaming-only icons; check product availability
                    if ((network as any).roamingOnly) return null
                    const isAvailable = network.id === 'all' || availableNetworks.has(network.id)
                    if (!isAvailable) return null
                  }

                  return (
                    <div key={network.id} className="flex flex-col items-center gap-2 flex-shrink-0" style={{ width: '80px' }}>
                      <button
                        onClick={() => setNetworkFilter(network.id)}
                        className={`flex items-center justify-center rounded-xl transition-all ${
                          networkFilter === network.id
                            ? 'bg-primary-100 border-2 border-primary-500 shadow-md'
                            : 'bg-white border-2 border-gray-200 hover:border-primary-300 hover:shadow-sm'
                        }`}
                        style={{ width: '76px', height: '76px', padding: `${(network as any).btnPadding ?? 10}px` }}
                      >
                        {network.logo ? (
                          <img
                            src={network.logo}
                            alt={network.name}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', transform: (network as any).imgScale ? `scale(${(network as any).imgScale})` : undefined, transformOrigin: 'center' }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                              const fallback = e.currentTarget.nextElementSibling as HTMLElement
                              if (fallback) fallback.style.display = 'flex'
                            }}
                          />
                        ) : null}
                        <div
                          className={`rounded-full flex items-center justify-center text-white font-bold text-xs bg-gradient-to-br from-primary-500 to-primary-400 ${
                            network.logo ? 'hidden' : ''
                          }`}
                          style={{ width: '100%', height: '100%' }}
                        >
                          {network.id === 'all' ? 'ALL' : network.name.substring(0, 2).toUpperCase()}
                        </div>
                      </button>
                      <span
                        className={`text-xs font-medium text-center leading-tight whitespace-nowrap ${
                          networkFilter === network.id ? 'text-primary-700' : 'text-gray-600'
                        }`}
                      >
                        {network.name}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Cart button only on shop/hardware page */
          <div className="flex justify-end mb-4">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/cart'}
              className="flex items-center"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Cart ({getItemCount()})
            </Button>
          </div>
        )}

        {/* World MultiNet zone sub-row — shown when World MultiNet is selected */}
        {networkFilter === 'world_multinet' && (
          <div className="mb-3 ml-1">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Select Zone</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setNetworkFilter('world_multinet')}
                className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all bg-primary-600 text-white border-primary-600 shadow-sm"
              >
                All Zones
              </button>
              {[1, 2, 3, 4, 5, 6].map((z) => {
                const zoneId = `roaming_zone${z}`
                const zoneAvailable = availableNetworks.has(zoneId)
                if (!zoneAvailable) return null
                return (
                  <button
                    key={zoneId}
                    onClick={() => setNetworkFilter(zoneId)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600"
                  >
                    Zone {z}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* MultiSim Subcategory Picker - shown when MultiSim network icon selected on other pages */}
        {showMultisimPicker && (
          <div className="flex items-center gap-4 mb-4">
            <span className="text-sm font-medium text-gray-500">Select MultiSim type:</span>
            <button
              onClick={() => setMultisimFilter('multisim_3net')}
              className={`flex flex-col items-center p-2 sm:p-3 rounded-xl transition-all ${
                multisimFilter === 'multisim_3net'
                  ? 'bg-purple-100 border-2 border-purple-500 shadow-md'
                  : 'bg-white border-2 border-gray-200 hover:border-purple-300 hover:shadow-sm'
              }`}
            >
              <img
                src="/images/networks/multisim-3net.png"
                alt="MultiSim 3 Networks"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex' }}
              />
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full items-center justify-center text-white font-bold text-xs hidden ${
                multisimFilter === 'multisim_3net' ? 'bg-purple-500' : 'bg-gray-400'
              }`}>3N</div>
              <span className={`text-xs sm:text-sm mt-1 font-medium text-center leading-tight ${
                multisimFilter === 'multisim_3net' ? 'text-purple-700' : 'text-gray-600'
              }`}>
                Vodafone / Three / O2
              </span>
            </button>

            <button
              onClick={() => setMultisimFilter('multisim_4net')}
              className={`flex flex-col items-center p-2 sm:p-3 rounded-xl transition-all ${
                multisimFilter === 'multisim_4net'
                  ? 'bg-purple-100 border-2 border-purple-500 shadow-md'
                  : 'bg-white border-2 border-gray-200 hover:border-purple-300 hover:shadow-sm'
              }`}
            >
              <img
                src="/images/networks/multisim-4net.png"
                alt="MultiSim 4 Networks"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex' }}
              />
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full items-center justify-center text-white font-bold text-xs hidden ${
                multisimFilter === 'multisim_4net' ? 'bg-purple-500' : 'bg-gray-400'
              }`}>4N</div>
              <span className={`text-xs sm:text-sm mt-1 font-medium text-center leading-tight ${
                multisimFilter === 'multisim_4net' ? 'text-purple-700' : 'text-gray-600'
              }`}>
                Vodafone / Three / O2 / EE
              </span>
            </button>
          </div>
        )}

        {/* Search By Filter Bar — hidden on hardware shop page */}
        {pageType !== 'shop' && <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-700">
            <Filter className="w-4 h-4" />
            <span className="text-sm font-semibold">Search By</span>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="contract-length-filter" className="text-sm text-gray-500">Contract Length:</label>
            <select
              id="contract-length-filter"
              value={contractLengthFilter}
              onChange={(e) => setContractLengthFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[40px] cursor-pointer"
            >
              <option value="all">All Contract Lengths</option>
              {pageType !== 'broadband' && <option value="payg">Pay&amp;Go (Month to Month)</option>}
              {pageType === 'broadband' && <option value="1">1 Month (Rolling Monthly)</option>}
              <option value="3">3 Month</option>
              <option value="6">6 Month</option>
              <option value="12">12 Month</option>
              <option value="18">18 Month</option>
              <option value="24">24 Month</option>
              <option value="36">36 Month</option>
            </select>
          </div>
          {contractLengthFilter !== 'all' && (
            <button
              onClick={() => setContractLengthFilter('all')}
              className="text-xs text-primary-600 hover:text-primary-800 underline ml-1"
            >
              Clear
            </button>
          )}
        </div>}
      </div>

      {addedProduct && (
        <Alert
          variant="success"
          message={`${addedProduct} added to cart`}
          onClose={() => setAddedProduct(null)}
          className="mb-6"
        />
      )}

      {error && (
        <Alert variant="error" message="Failed to load products. Please try again." className="mb-6" />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredAndSortedProducts?.map((product) => (
          <Card key={product.id} className="flex flex-col p-4 sm:p-6">
            {product.image_url ? (
              <div
                className="relative mb-4 cursor-pointer group"
                onClick={() => navigate(`/product/${product.slug || product.id}`)}
              >
                <img
                  src={product.image_url}
                  alt={product.name}
                  loading="eager"
                  decoding="async"
                  className="w-full object-contain rounded-lg bg-gray-50 transform-gpu transition-transform group-hover:scale-105"
                  style={{ height: `${192 * ((product.image_display_size || 100) / 100)}px` }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    e.currentTarget.nextElementSibling?.classList.remove('hidden')
                  }}
                />
                <div
                  className="hidden flex items-center justify-center bg-gray-100 rounded-lg"
                  style={{ height: `${192 * ((product.image_display_size || 100) / 100)}px` }}
                >
                  {getProductIcon(product.product_type)}
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 font-medium bg-primary-600 px-3 py-1 rounded-full text-sm transition-opacity">
                    View Details
                  </span>
                </div>
              </div>
            ) : (
              <div
                className="flex items-center justify-center mb-4 cursor-pointer"
                onClick={() => navigate(`/product/${product.slug || product.id}`)}
              >
                {getProductIcon(product.product_type)}
              </div>
            )}

            <h3
              className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 cursor-pointer hover:text-primary-600 transition-colors"
              onClick={() => navigate(`/product/${product.slug || product.id}`)}
            >
              {product.name}
            </h3>

            {product.short_description && (
              <p className="text-sm sm:text-base text-gray-600 mb-4">{product.short_description}</p>
            )}

            <div className="flex-grow">
              {product.network_type && (
                <p className="text-sm text-gray-500 mb-2">
                  Network:{' '}
                  <span className="font-medium capitalize">
                    {product.network_type.replace('_', ' ')}
                  </span>
                </p>
              )}
              {product.is_contract && product.contract_months && (
                <p className="text-sm text-gray-500 mb-2">
                  Contract: <span className="font-medium">{product.contract_months === 1 ? "Rolling monthly (30 days' notice)" : `${product.contract_months} months`}</span>
                </p>
              )}
            </div>

            <div className="mt-4 pt-4 border-t">
              <div className="flex items-baseline justify-between mb-3 sm:mb-4">
                <div>
                  {product.discounted_price && product.discounted_price < product.price ? (
                    <>
                      <span className="text-xl sm:text-2xl font-bold text-primary-600">
                        £{product.discounted_price.toFixed(2)}
                      </span>
                      <span className="text-base sm:text-lg text-gray-400 line-through ml-2">
                        £{product.price.toFixed(2)}
                      </span>
                    </>
                  ) : (
                    <span className="text-xl sm:text-2xl font-bold text-primary-600">
                      £{product.price.toFixed(2)}
                    </span>
                  )}
                </div>
                {product.is_contract && (
                  <span className="text-xs sm:text-sm text-gray-500">/month</span>
                )}
              </div>

              {product.admin_fee_enabled && product.admin_fee && (
                <p className="text-xs text-gray-500 mb-3">
                  + £{product.admin_fee.toFixed(2)} {product.product_type === 'broadband' ? 'one-time Refundable Holding Deposit - Refunded at the end of the Contract' : 'admin fee'}
                </p>
              )}

              <Button fullWidth size="md" onClick={() => handleAddToCart(product)}>
                Add to Cart
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {filteredAndSortedProducts?.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">No products found for this network.</p>
        </div>
      )}
    </div>
  )
}

export default Shop
